// Central content for GenieX — used across pages for consistency & easy editing.

export const BRAND = {
  name: "GenieX",
  tagline: "Intelligence at Your Command",
  sub: "Intelligent Living",
  phone: "+91 90636 62999",
  phoneRaw: "+919063662999",
  email: "hello@geniex.in",
  address: "3rd Floor, KNR Square, Door No. 1-60, opp. The Platina, Gachibowli, Hyderabad, Telangana 500032",
  social: {
    instagram: "#",
    facebook: "#",
    youtube: "#",
    linkedin: "#",
  },
};

export const NAV = [
  { label: "Home", to: "/" },
  { label: "Dynamic Series", to: "/dynamic-series" },
  { label: "Solutions", to: "/solutions" },
  { label: "Intelligent Living", to: "/intelligent-living" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const HERO = {
  eyebrow: "Introducing GenieX",
  title: "Intelligence\nAt Your Command",
  sub: "A home that responds before you ask.",
  primary: { label: "Book a Consultation", to: "/contact" },
  secondary: { label: "Explore Intelligent Living", to: "/solutions" },
  // Cinematic luxury villa, night, warm interior lighting
  image:
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=85",
};

// Dynamic Series scenes — image-led storytelling.
// Each scene now uses a real product image with an "elevating" description
// (no generic scene names — the copy speaks to the image itself).
export const SCENES = [
  {
    key: "design",
    label: "Crafted to Disappear",
    title: "Crafted to Disappear",
    sub:
      "Elevates the visual character of your home — sculpted glass, precision-milled aluminium, designed to disappear into the wall.",
    image:
      "https://customer-assets.emergentagent.com/job_intelligent-living-8/artifacts/j9vp14qh_457768.jpg",
  },
  {
    key: "finishes",
    label: "Made in Many Moods",
    title: "Made in Many Moods",
    sub:
      "Available in multiple finishes — curated to complement any palette of interiors, from minimalist whites to deep, warm blacks.",
    image:
      "https://customer-assets.emergentagent.com/job_intelligent-living-8/artifacts/8st34l65_455060.jpg",
  },
  {
    key: "scenes",
    label: "Scenes, Your Way",
    title: "Scenes, Your Way",
    sub:
      "Easy-to-customise scenes through the GenieX app — set the mood for every moment of your day with a single, effortless tap.",
    image:
      "https://customer-assets.emergentagent.com/job_intelligent-living-8/artifacts/l90gxjmg_455044.jpg",
  },
  {
    key: "ambient",
    label: "Ambient Intelligence",
    title: "Ambient Intelligence",
    sub:
      "An interactive display that quietly glows with time, weather and your favourite scenes — present when you need it, invisible when you don't.",
    image:
      "https://customer-assets.emergentagent.com/job_intelligent-living-8/artifacts/o3zkzexk_455058.jpg",
  },
];

export const LIVING_CATEGORIES = [
  {
    key: "comfort",
    title: "Comfort",
    sub: "The perfect atmosphere.",
    icon: "Sun",
    body:
      "Light, climate and ambience that adapt to the time of day, the mood of the moment, and the people in the room.",
  },
  {
    key: "security",
    title: "Security",
    sub: "Peace of mind.",
    icon: "ShieldCheck",
    body:
      "CCTV, smart locks, video door phones and presence sensors — all working together, all visible from a single elegant app.",
  },
  {
    key: "safety",
    title: "Safety",
    sub: "Protection when it matters most.",
    icon: "AlarmSmoke",
    body:
      "Gas, smoke and water-leak detection that act silently, instantly, and automatically — before anyone needs to react.",
  },
  {
    key: "entertainment",
    title: "Entertainment",
    sub: "Experiences worth gathering for.",
    icon: "Film",
    body:
      "Home theatre automation, multi-room audio, lighting scenes and AV control unified into one effortless gesture.",
  },
  {
    key: "access",
    title: "Access",
    sub: "Seamless movement through every space.",
    icon: "KeyRound",
    body:
      "Smart locks, gate automation, boom barriers and access control — for villas, gated estates and commercial spaces.",
  },
];

export const DYNAMIC_FEATURES = [
  { title: "Interactive Display", sub: "Live time, weather, scenes and contextual cues — quietly visible at a glance.", icon: "MonitorSmartphone" },
  { title: "Personalized Scenes", sub: "Relax. Movie. Away. Good Night. Crafted to your lifestyle, one touch away.", icon: "Sparkles" },
  { title: "Instant Control", sub: "Light, fans, AC, curtains and entertainment — every room, every moment.", icon: "MousePointerClick" },
  { title: "Elegant Design", sub: "Glass. Leather. Precision-milled aluminium. Designed to disappear into the wall.", icon: "Gem" },
];

export const ECOSYSTEM = [
  { name: "Dynamic Series", icon: "PanelTop", slug: "dynamic-series", cat: "Switches" },
  { name: "Smart Touch Panels", icon: "Tablet", slug: "smart-touch-panels", cat: "Switches" },
  { name: "Smart Switches", icon: "ToggleRight", slug: "smart-switches", cat: "Switches" },
  { name: "Dimmers", icon: "SlidersHorizontal", slug: "dimmers", cat: "Lighting" },
  { name: "Scene Controllers", icon: "LayoutGrid", slug: "scene-controllers", cat: "Control" },
  { name: "Smart Locks", icon: "Lock", slug: "smart-locks", cat: "Security" },
  { name: "Video Door Phones", icon: "DoorOpen", slug: "video-door-phones", cat: "Security" },
  { name: "CCTV Systems", icon: "Cctv", slug: "cctv-solutions", cat: "Security" },
  { name: "Motion Sensors", icon: "Radar", slug: "motion-sensors", cat: "Sensors" },
  { name: "Presence Sensors", icon: "ScanFace", slug: "presence-sensors", cat: "Sensors" },
  { name: "Curtain Automation", icon: "Wind", slug: "curtain-automation", cat: "Automation" },
  { name: "Blind Automation", icon: "AlignHorizontalDistributeCenter", slug: "blind-automation", cat: "Automation" },
  { name: "AC Control", icon: "Snowflake", slug: "ac-control", cat: "Climate" },
  { name: "TV Control", icon: "Tv", slug: "tv-control", cat: "Entertainment" },
  { name: "Home Theatre Automation", icon: "Theater", slug: "home-theater-automation", cat: "Entertainment" },
  { name: "Alexa Integration", icon: "Mic", slug: "alexa-integration", cat: "Voice" },
  { name: "Google Home", icon: "Mic2", slug: "google-home-integration", cat: "Voice" },
  { name: "Gas Leak Sensors", icon: "Flame", slug: "gas-leak-detection", cat: "Safety" },
  { name: "Water Leak Sensors", icon: "Droplets", slug: "water-leak-detection", cat: "Safety" },
  { name: "Smoke Sensors", icon: "CloudFog", slug: "smoke-sensors", cat: "Safety" },
  { name: "Robot Vacuum", icon: "BotMessageSquare", slug: "robot-vacuum", cat: "Appliances" },
  { name: "Pet Feeders", icon: "Bone", slug: "pet-feeders", cat: "Appliances" },
  { name: "Gate Automation", icon: "Fence", slug: "gate-automation", cat: "Access" },
  { name: "Boom Barriers", icon: "AlignVerticalSpaceAround", slug: "boom-barriers", cat: "Access" },
  { name: "Access Control", icon: "Fingerprint", slug: "access-control", cat: "Access" },
  { name: "Mobile App Control", icon: "Smartphone", slug: "mobile-app", cat: "Control" },
  { name: "Scheduling & Scenes", icon: "CalendarClock", slug: "scheduling-scenes", cat: "Control" },
  { name: "KNX Automation", icon: "Cable", slug: "knx-automation", cat: "Backbone" },
  { name: "WiFi Automation", icon: "Wifi", slug: "wifi-automation", cat: "Backbone" },
];

export const SOLUTIONS_TIERS = [
  {
    key: "retro",
    badge: "Existing Home?",
    title: "Retro Module",
    sub: "No rewiring required.",
    price: "Starting ₹1.5L+",
    body:
      "Transform your existing home into an intelligent space without breaking a single wall. Our retro modules hide behind your current switches.",
    color: "from-amber-100/5 to-transparent",
  },
  {
    key: "smart-touch",
    badge: "Building New?",
    title: "Smart Touch",
    sub: "Elegant touch panels.",
    price: "₹3L – ₹6L+",
    body:
      "Replace every switch in your home with a beautifully crafted glass touch panel. Personalized scenes, scheduling, and instant control.",
    color: "from-amber-200/8 to-transparent",
    featured: true,
  },
  {
    key: "knx",
    badge: "Luxury Villa?",
    title: "KNX Wired",
    sub: "International standard.",
    price: "₹6L+",
    body:
      "The world's most reliable wired automation protocol. Used in palaces, super-yachts and seven-star hotels. The gold standard for villas.",
    color: "from-amber-300/10 to-transparent",
  },
];

export const WHY_GENIEX = [
  { title: "Premium Products", sub: "World-class quality, curated by us.", icon: "BadgeCheck" },
  { title: "Expert Installation", sub: "Certified technicians. Zero damage.", icon: "HardHat" },
  { title: "Seamless Integration", sub: "Works with what you already love.", icon: "PlugZap" },
  { title: "App Control Anywhere", sub: "Your home, in your pocket.", icon: "Smartphone" },
  { title: "Voice Control", sub: "Alexa and Google Home, natively.", icon: "Mic" },
  { title: "Reliable Support", sub: "5-year warranty. Lifetime care.", icon: "LifeBuoy" },
];

export const LOCATIONS = [
  { slug: "hyderabad", name: "Hyderabad" },
  { slug: "secunderabad", name: "Secunderabad" },
  { slug: "gachibowli", name: "Gachibowli" },
  { slug: "jubilee-hills", name: "Jubilee Hills" },
  { slug: "banjara-hills", name: "Banjara Hills" },
  { slug: "kondapur", name: "Kondapur" },
  { slug: "kukatpally", name: "Kukatpally" },
  { slug: "warangal", name: "Warangal" },
  { slug: "karimnagar", name: "Karimnagar" },
  { slug: "vijayawada", name: "Vijayawada" },
  { slug: "visakhapatnam", name: "Visakhapatnam" },
  { slug: "tirupati", name: "Tirupati" },
  { slug: "guntur", name: "Guntur" },
  { slug: "rajahmundry", name: "Rajahmundry" },
  { slug: "kakinada", name: "Kakinada" },
];

export const BLOG_POSTS = [
  {
    slug: "home-automation-cost-guide-india",
    title: "Home Automation Cost Guide for India (2026)",
    excerpt:
      "How much should you really budget for a smart home in India? A transparent breakdown across apartments, villas and luxury homes.",
    image:
      "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=2000&q=80",
    date: "Dec 2025",
    read: "8 min",
    body: [
      "The honest answer: home automation in India can start at ₹1.5 lakh for an apartment and easily cross ₹50 lakh for a luxury villa.",
      "What changes is not just the size of the home — it's the philosophy. WiFi automation is flexible and friendly. KNX is the wired international standard, used in palaces and super-yachts.",
      "At GenieX, our three-tier approach is built to match three different lifestyles: Retro Module for existing homes, Smart Touch for new builds, and KNX Wired for luxury villas.",
    ],
  },
  {
    slug: "knx-vs-wifi-automation",
    title: "KNX vs WiFi Automation — Which is right for you?",
    excerpt:
      "The most asked question in luxury home automation. We break down reliability, flexibility, scale and cost in plain language.",
    image:
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=2000&q=80",
    date: "Nov 2025",
    read: "6 min",
    body: [
      "KNX is a wired, decentralised, international standard with 30+ years of reliability. It is what palaces, embassies and seven-star hotels use.",
      "WiFi automation is faster to deploy, gentler on the wallet, and perfect for existing homes and apartments.",
      "Our advice: if you are building a villa from scratch and want a system that will outlive your home loan, choose KNX. For everything else, modern WiFi automation is wonderful.",
    ],
  },
  {
    slug: "smart-home-ideas-for-villas",
    title: "Smart Home Ideas for Villas",
    excerpt: "Ten ways a villa can be quietly transformed by intelligence — without ever feeling like a tech showroom.",
    image:
      "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=2000&q=80",
    date: "Nov 2025",
    read: "7 min",
    body: [
      "Gate automation that recognises your car. Driveway lighting that wakes up as you arrive.",
      "Lutron-quality dimming on every chandelier. Curtain automation in every bedroom.",
      "A single app for the entire estate — gates, pool, garden, security, climate.",
    ],
  },
  {
    slug: "smart-home-ideas-for-apartments",
    title: "Smart Home Ideas for Apartments",
    excerpt: "You don't need a villa to live intelligently. Here is what every premium apartment owner should consider.",
    image:
      "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=2000&q=80",
    date: "Oct 2025",
    read: "5 min",
    body: [
      "Start with the four touch-points that matter most: entry, living, bedroom, bathroom.",
      "Smart locks, smart lighting, AC control and curtain automation — in that order.",
      "The retro module approach means no rewiring and no damage to your interiors.",
    ],
  },
  {
    slug: "benefits-of-smart-lighting",
    title: "Benefits of Smart Lighting",
    excerpt: "Why lighting is the easiest, most rewarding way to begin your intelligent living journey.",
    image:
      "https://images.unsplash.com/photo-1565538810643-b5bdb714032a?auto=format&fit=crop&w=2000&q=80",
    date: "Oct 2025",
    read: "4 min",
    body: ["Mood. Health. Energy savings. The case for smart lighting is overwhelming."],
  },
  {
    slug: "home-theater-automation-guide",
    title: "Home Theatre Automation Guide",
    excerpt: "Build a cinema at home that turns on with one touch — and disappears when you don't need it.",
    image:
      "https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?auto=format&fit=crop&w=2000&q=80",
    date: "Sep 2025",
    read: "9 min",
    body: ["From single-button scenes to dedicated theatres with rising screens, acoustic treatment and luxury seating."],
  },
  {
    slug: "smart-locks-explained",
    title: "Smart Locks Explained",
    excerpt: "Face recognition, fingerprint, RFID, mobile, key — six ways into a modern home.",
    image:
      "https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=2000&q=80",
    date: "Sep 2025",
    read: "5 min",
    body: ["The lock is the first impression of your home. It deserves more thought than a hardware-store visit."],
  },
  {
    slug: "cctv-buying-guide",
    title: "CCTV Buying Guide for Homes",
    excerpt: "Resolution, storage, night vision, cloud — what actually matters when choosing CCTV for your home.",
    image:
      "https://images.unsplash.com/photo-1565043666747-69f6646db940?auto=format&fit=crop&w=2000&q=80",
    date: "Aug 2025",
    read: "6 min",
    body: ["Most homes are over-cameraed and under-monitored. Here is how to think about coverage."],
  },
  {
    slug: "curtain-automation-guide",
    title: "Curtain Automation Guide",
    excerpt: "Whisper-quiet motors, voice control and scenes — the most delightful upgrade in a smart home.",
    image:
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=2000&q=80",
    date: "Aug 2025",
    read: "5 min",
    body: ["Wake up to gentle sunlight. Close the bedroom with one tap. Live like a hotel suite."],
  },
  {
    slug: "luxury-home-technology-trends-2026",
    title: "Luxury Home Technology Trends 2026",
    excerpt: "Where premium homes are going next — and what we are quietly building for our clients.",
    image:
      "https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?auto=format&fit=crop&w=2000&q=80",
    date: "Jul 2025",
    read: "8 min",
    body: ["Ambient computing. Voice-less control. Predictive routines. A glimpse of the next five years."],
  },
];

export const PRODUCT_PAGES = [
  { slug: "dynamic-series", title: "Dynamic Series", tagline: "The switch, reimagined.", hero: "/renders/product-switch.png" },
  { slug: "smart-touch-panels", title: "Smart Touch Panels", tagline: "Glass. Light. Effortless control.", hero: "/renders/scene-goodnight.png" },
  { slug: "smart-locks", title: "Smart Locks", tagline: "Six ways in. One peace of mind.", hero: "https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=2000&q=80" },
  { slug: "cctv-solutions", title: "CCTV Solutions", tagline: "Seen. Recorded. Reassured.", hero: "https://images.unsplash.com/photo-1565043666747-69f6646db940?auto=format&fit=crop&w=2000&q=80" },
  { slug: "video-door-phones", title: "Video Door Phones", tagline: "See your door from anywhere.", hero: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=2000&q=80" },
  { slug: "curtain-automation", title: "Curtain Automation", tagline: "Light, on your terms.", hero: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=2000&q=80" },
  { slug: "home-theater-automation", title: "Home Theatre Automation", tagline: "One touch. Lights down. Curtain up.", hero: "https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?auto=format&fit=crop&w=2000&q=80" },
  { slug: "motion-sensors", title: "Motion Sensors", tagline: "Quietly aware. Always.", hero: "https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=2000&q=80" },
  { slug: "gas-leak-detection", title: "Gas Leak Detection", tagline: "Safety, on standby.", hero: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=2000&q=80" },
  { slug: "water-leak-detection", title: "Water Leak Detection", tagline: "Before a drop becomes a disaster.", hero: "https://images.unsplash.com/photo-1605713288610-00c4b8d8bdee?auto=format&fit=crop&w=2000&q=80" },
  { slug: "gate-automation", title: "Gate Automation", tagline: "Arrive without slowing down.", hero: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=2000&q=80" },
  { slug: "boom-barriers", title: "Boom Barriers", tagline: "Controlled access. Effortless flow.", hero: "https://images.unsplash.com/photo-1565043666747-69f6646db940?auto=format&fit=crop&w=2000&q=80" },
  { slug: "knx-automation", title: "KNX Automation", tagline: "The international standard.", hero: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=2000&q=80" },
  { slug: "wifi-automation", title: "WiFi Automation", tagline: "Wireless. Effortless. Premium.", hero: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=2000&q=80" },
];

export const POLICIES = [
  { slug: "privacy-policy", title: "Privacy Policy" },
  { slug: "terms-of-service", title: "Terms of Service" },
  { slug: "refund-policy", title: "Refund Policy" },
  { slug: "shipping-policy", title: "Shipping & Installation Policy" },
  { slug: "warranty-policy", title: "Warranty Policy" },
  { slug: "cookie-policy", title: "Cookie Policy" },
];
